import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCall } from '../context/CallContext';
import { IconMic, IconPhone, IconVolume, IconChevronLeft } from '../components/talkAppleIcons';
import useConversationViewport from '../hooks/useConversationViewport';
import './conversation.css';
import './private-call.css';

const fmt = seconds => `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
export default function PrivateCall() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const call = useCall();
  const viewportRef = useConversationViewport();
  const [partner, setPartner] = useState(null);
  useEffect(() => { call.startCall(partnerId, user); }, [partnerId, user.id, call.startCall]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/users/${partnerId}`).then(({ data }) => {
      if (cancelled) return;
      setPartner(data.user);
      call.setActiveCall(previous => previous && String(previous.partnerId) === String(partnerId) ? { ...previous, partnerName: data.user.username, partnerAvatar: data.user.avatar } : previous);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [partnerId, call.setActiveCall]);
  const terminal = call.callStatus === 'failed' || call.callStatus === 'ended';
  const name = partner?.username || call.activeCall?.partnerName || '会話相手';
  const avatar = partner?.avatar || call.activeCall?.partnerAvatar;
  const finish = () => { call.endCall(); navigate(`/chat/${partnerId}`, { replace: true }); };
  const micUnavailable = call.callStatus === 'failed' && call.callMessage.includes('マイク');
  const status = micUnavailable ? 'マイクを使えません' : call.callStatus === 'failed' ? '接続できませんでした' : call.callMessage;
  return <main ref={viewportRef} className="jt-private-call" data-call-state={call.callStatus}>
    <header className="jt-call-header">
      <button data-control="icon" onClick={terminal ? finish : () => navigate('/chat')} aria-label={terminal ? '通話画面を閉じる' : '通話を続けながらチャット一覧へ'}><IconChevronLeft size={24} /></button>
      <span>音声通話</span>
      <span className="jt-call-header-spacer" aria-hidden="true" />
    </header>
    <section className="jt-call-person" aria-label="通話相手">
      <div className="jt-call-avatar">{avatar ? <img src={avatar} alt="" /> : <span>{name[0]?.toUpperCase()}</span>}</div>
      <h1>{name}</h1>
      <p className="jt-call-status" role="status">{status}</p>
      {call.callStatus === 'failed' && <p className="jt-call-help">{micUnavailable ? '端末のマイク設定を確認してから、もう一度お試しください。' : call.callMessage}</p>}
      {(call.callStatus === 'connected' || call.callStatus === 'ended' && call.duration > 0) && <p className="jt-call-time" aria-label={`通話時間 ${fmt(call.duration)}`}>{fmt(call.duration)}</p>}
    </section>
    <section className="jt-call-actions" aria-label="通話の操作">
      {terminal ? <div className="jt-call-finished-actions">
        {call.callStatus === 'failed' && <button data-control="primary" className="jt-call-retry" onClick={() => call.startCall(partnerId, user)}><IconPhone size={20} />もう一度かける</button>}
        <button data-control="text" className="jt-call-return" onClick={finish}>チャットに戻る</button>
      </div> : <div className="jt-call-controls">
        <button data-control="secondary" className="jt-call-control" aria-label={call.micOn ? 'マイクをオフ' : 'マイクをオン'} aria-pressed={!call.micOn} disabled={!call.localStreamRef.current} onClick={call.toggleMic}>
          <span className="jt-call-control-icon" data-off={!call.micOn}><IconMic size={24} /></span><span>{call.micOn ? 'マイク' : 'ミュート中'}</span>
        </button>
        <button data-control="secondary" className="jt-call-control" aria-label={call.remoteAudioOn ? '音声を消す' : '音声を戻す'} aria-pressed={!call.remoteAudioOn} disabled={!call.localStreamRef.current} onClick={call.toggleRemoteAudio}>
          <span className="jt-call-control-icon" data-off={!call.remoteAudioOn}><IconVolume size={24} /></span><span>{call.remoteAudioOn ? '相手の音声' : '音声オフ'}</span>
        </button>
        <button data-control="destructive" className="jt-call-control jt-call-end" aria-label="通話を終了" onClick={finish}><span className="jt-call-control-icon"><IconPhone size={24} /></span><span>終了</span></button>
      </div>}
    </section>
  </main>;
}
